/**
 * "I can't remember what I used."
 *
 * The end of the line for a parent who has tried both their addresses and their child's.
 * It answers with a masked address (enough to recognise, never enough to learn) and
 * sends the recovery link itself, so knowing the answer is never a substitute for
 * reaching the mailbox.
 */
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SchoolAutocomplete } from "@/components/SchoolAutocomplete";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import { ArrowLeft, MailCheck, SearchX } from "lucide-react";

interface Result {
  found: boolean;
  maskedEmail?: string;
  maskedUsername?: string | null;
  legacy?: boolean;
  message: string;
}

export default function FindAccount() {
  const [form, setForm] = useState({
    firstName: "", lastName: "", school: "", grade: "", parentEmail: "", parentPhone: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const { toast } = useToast();

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);
    try {
      const body: Record<string, string> = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      };
      // Only send what was filled in: an empty string is a detail that matches nothing.
      for (const key of ["school", "parentEmail", "parentPhone", "grade"] as const) {
        if (form[key].trim()) body[key] = form[key].trim();
      }
      const res = await apiRequest("POST", "/api/auth/find-account", body);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to look that up");
      setResult(data);
    } catch (error: any) {
      toast({ title: "Couldn't look that up", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (result?.found) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MailCheck className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>We found the account</CardTitle>
            <CardDescription>
              It's registered to <strong data-testid="text-masked-email">{result.maskedEmail}</strong>
              {result.maskedUsername ? <>, username <strong>{result.maskedUsername}</strong></> : null}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {result.legacy
                ? "That's an account from the old EduFocal site. We've emailed a link to set a password and bring it across. It expires in an hour."
                : "We've emailed a password reset link to that address. It expires in an hour."}
            </p>
            <Link href="/login">
              <Button className="w-full" data-testid="button-back-login">Back to sign in</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Find your account</CardTitle>
          <CardDescription>
            Tell us the student's name and one more detail, and we'll tell you which email
            address the account is on, and send a link to it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Student's first name</Label>
                <Input id="firstName" value={form.firstName} onChange={(e) => set("firstName", e.target.value)}
                  required minLength={2} disabled={isLoading} data-testid="input-first-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Student's last name</Label>
                <Input id="lastName" value={form.lastName} onChange={(e) => set("lastName", e.target.value)}
                  required minLength={2} disabled={isLoading} data-testid="input-last-name" />
              </div>
            </div>

            <p className="text-sm font-medium pt-2">And at least one of these:</p>

            <div className="space-y-2">
              <Label htmlFor="school">School</Label>
              <SchoolAutocomplete id="school" value={form.school} onChange={(v) => set("school", v)}
                disabled={isLoading} data-testid="input-school" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="grade">Grade</Label>
                <Select value={form.grade} onValueChange={(v) => set("grade", v)} disabled={isLoading}>
                  <SelectTrigger id="grade" data-testid="select-grade">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {[4, 5, 6].map(g => <SelectItem key={g} value={String(g)}>Grade {g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="parentPhone">Parent's phone</Label>
                <Input id="parentPhone" type="tel" placeholder="876 555 1234" value={form.parentPhone}
                  onChange={(e) => set("parentPhone", e.target.value)} disabled={isLoading} data-testid="input-parent-phone" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="parentEmail">Parent's email</Label>
              <Input id="parentEmail" type="email" placeholder="parent@example.com" value={form.parentEmail}
                onChange={(e) => set("parentEmail", e.target.value)} disabled={isLoading} data-testid="input-parent-email" />
            </div>

            {result && !result.found && (
              <div className="flex gap-3 rounded-lg border border-border bg-muted/40 p-3" data-testid="text-not-found">
                <SearchX className="h-5 w-5 shrink-0 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{result.message}</p>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading} data-testid="button-find">
              {isLoading ? "Looking..." : "Find my account"}
            </Button>

            <Link href="/login">
              <Button variant="ghost" className="w-full" data-testid="link-back-login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
